import Base from '../base/base';
import './design-configurator-beta.scss';

interface DesignConfiguratorBetaOptions {
  debug?: boolean;
  rootSelector?: string;
  toggleSelector?: string;
  panelSelector?: string;
  optionSelector?: string;
  imageSelector?: string;
}

interface PanelState {
  panel: HTMLElement;
  options: HTMLAnchorElement[];
  images: HTMLElement[];
}

export default class DesignConfiguratorBeta extends Base {
  private rootSelector: string;
  private toggleSelector: string;
  private panelSelector: string;
  private optionSelector: string;
  private imageSelector: string;

  private root: HTMLElement | null = null;
  private panels: PanelState[] = [];
  private toggleButtons: HTMLAnchorElement[] = [];

  private boundOnToggleClick: (e: Event) => void;
  private boundOnOptionClick: (e: Event) => void;

  constructor(options: DesignConfiguratorBetaOptions = {}) {
    super(options.debug || false);
    this.rootSelector = options.rootSelector || '[data-design-configurator-group]';
    this.toggleSelector = options.toggleSelector || '.segmented-control__button';
    this.panelSelector = options.panelSelector || '[data-design-configurator]';
    this.optionSelector = options.optionSelector || '[data-design-option]';
    this.imageSelector = options.imageSelector || '[data-design-label]';

    this.boundOnToggleClick = this.onToggleClick.bind(this);
    this.boundOnOptionClick = this.onOptionClick.bind(this);

    this.init();
  }

  init(): void {
    this.cacheDOM();
    this.log('DesignConfiguratorBeta cacheDOM complete', {
      hasRoot: !!this.root,
      toggles: this.toggleButtons.length,
      panels: this.panels.length
    });

    if (!this.root || this.panels.length === 0) {
      this.log('DesignConfiguratorBeta: root or panels not found');
      return;
    }

    // `coi-attract` pulses the active options (see shared/styles/_mixins.scss)
    // until the user makes their first selection.
    this.root.classList.add('dc-beta-root', 'coi-attract');
    this.bindEvents();
    this.log('DesignConfiguratorBeta bindEvents complete');
    this.setInitialState();
    this.log('DesignConfiguratorBeta setInitialState complete');
    this.log('DesignConfiguratorBeta initialized');
  }

  private cacheDOM(): void {
    this.root = document.querySelector<HTMLElement>(this.rootSelector);
    if (!this.root) return;

    this.toggleButtons = Array.from(this.root.querySelectorAll<HTMLAnchorElement>(this.toggleSelector));

    const panels = Array.from(this.root.querySelectorAll<HTMLElement>(this.panelSelector));
    this.panels = panels.map((panel) => this.buildPanelState(panel));
  }

  private buildPanelState(panel: HTMLElement): PanelState {
    const options = Array.from(panel.querySelectorAll<HTMLAnchorElement>(this.optionSelector));
    const images = Array.from(panel.querySelectorAll<HTMLElement>(this.imageSelector));

    panel.classList.add('dc-beta-panel');
    options.forEach((option) => option.classList.add('dc-beta-option'));
    images.forEach((image) => image.classList.add('dc-beta-image'));

    return { panel, options, images };
  }

  private bindEvents(): void {
    this.toggleButtons.forEach((button) => button.addEventListener('click', this.boundOnToggleClick));
    this.panels.forEach(({ panel }) => panel.addEventListener('click', this.boundOnOptionClick));
  }

  private setInitialState(): void {
    this.setActivePanel(0);
  }

  private onToggleClick(e: Event): void {
    const target = e.target as HTMLElement;
    if (!target) return;

    const button = target.closest(this.toggleSelector) as HTMLAnchorElement | null;
    if (!button) return;

    e.preventDefault();

    const index = this.toggleButtons.indexOf(button);
    this.log('Segmented control clicked', { index });

    if (index === -1) return;

    this.setActivePanel(index);
  }

  private onOptionClick(e: Event): void {
    const target = e.target as HTMLElement;
    if (!target) return;

    const option = target.closest(this.optionSelector) as HTMLAnchorElement | null;
    if (!option) return;

    e.preventDefault();

    const panel = this.panels.find(({ panel: element }) => element.contains(option));
    if (!panel) return;

    const value = option.getAttribute('data-design-option');
    if (!value) return;

    this.setActiveDesign(panel, value);
    this.root?.classList.remove('coi-attract');
  }

  private setActivePanel(index: number): void {
    this.log('Set active panel', { index });

    const activePanel = this.panels[index];
    if (!activePanel) return;

    this.panels.forEach(({ panel }, i) => {
      panel.classList.toggle('dc-beta-panel--active', i === index);
    });

    this.toggleButtons.forEach((button, i) => {
      button.classList.toggle('dc-beta-toggle--active', i === index);
    });

    this.ensureDefaultSelection(activePanel);
  }

  private ensureDefaultSelection(panel: PanelState): void {
    const activeOption = panel.options.find((option) => option.classList.contains('coi--active'));
    if (activeOption) return;

    const firstOption = panel.options[0];
    if (!firstOption) return;

    const value = firstOption.getAttribute('data-design-option');
    if (!value) return;

    this.setActiveDesign(panel, value);
  }

  private setActiveDesign(panel: PanelState, value: string): void {
    const targetValue = value.toLowerCase();

    panel.options.forEach((option) => {
      const optionValue = option.getAttribute('data-design-option')?.toLowerCase();
      option.classList.toggle('coi--active', optionValue === targetValue);
    });

    panel.images.forEach((image) => {
      const imageValue = image.getAttribute('data-design-label')?.toLowerCase();
      image.classList.toggle('dc-beta-image--active', imageValue === targetValue);
    });
  }

  public destroy(): void {
    this.toggleButtons.forEach((button) => button.removeEventListener('click', this.boundOnToggleClick));
    this.panels.forEach(({ panel }) => panel.removeEventListener('click', this.boundOnOptionClick));

    if (this.root) {
      this.root.classList.remove('dc-beta-root', 'coi-attract');
    }

    this.toggleButtons = [];
    this.panels = [];
    this.root = null;
  }
}
